import { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { broadcastService, toBroadcastMessage } from "@/services/broadcast.service";
import type {
  BroadcastApiType,
  BroadcastMessageRequest,
  BroadcastMessageResponse,
} from "@/types/broadcast.types";
import type { BroadcastMessage } from "../BroadcastBanner";

type BroadcastData = {
  subjectDe: string;
  subjectEn: string;
  messageDe: string;
  messageEn: string;
  type: "info" | "warning" | "alert";
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
};

interface BroadcastManagementPanelProps {
  onScheduleBroadcast: (broadcast: BroadcastMessage) => void;
}

export function BroadcastManagementPanel({
  onScheduleBroadcast,
}: BroadcastManagementPanelProps) {
  const { t, language } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const [broadcastData, setBroadcastData] = useState<BroadcastData>({
    subjectDe: "",
    subjectEn: "",
    messageDe: "",
    messageEn: "",
    type: "info",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
  });
  const [broadcasts, setBroadcasts] = useState<BroadcastMessageResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const toApiType = (value: BroadcastData["type"]): BroadcastApiType =>
    value.toUpperCase() as BroadcastApiType;

  const getLocalizedText = (de: string, en?: string | null): string => {
    if (language === "en") {
      return en && en.trim().length > 0 ? en : de;
    }
    return de;
  };

  useEffect(() => {
    let cancelled = false;

    const loadBroadcasts = async () => {
      try {
        const data = await broadcastService.getAdminBroadcasts();
        if (!cancelled) {
          setBroadcasts(data);
        }
      } catch (error: any) {
        if (!cancelled) {
          setBroadcasts([]);
        }
        toast.error(error.response?.data?.message ?? t("common.genericError"));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadBroadcasts();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleScheduleBroadcast = () => {
    const { subjectDe, messageDe, startDate, startTime, endDate, endTime } =
      broadcastData;

    if (
      !subjectDe ||
      !messageDe ||
      !startDate ||
      !startTime ||
      !endDate ||
      !endTime
    ) {
      toast.error(t("admin.fillAllFields"));
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    if (endDateTime <= startDateTime) {
      toast.error(t("admin.endAfterStart"));
      return;
    }

    const payload: BroadcastMessageRequest = {
      subjectDe: broadcastData.subjectDe,
      subjectEn: broadcastData.subjectEn || broadcastData.subjectDe,
      messageDe: broadcastData.messageDe,
      messageEn: broadcastData.messageEn || broadcastData.messageDe,
      type: toApiType(broadcastData.type),
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
    };

    const schedule = async () => {
      try {
        const created = await broadcastService.createBroadcast(payload);
        setBroadcasts((prev) => [created, ...prev]);
        onScheduleBroadcast(toBroadcastMessage(created));
        toast.success(t("admin.broadcastScheduled"), {
          description: `${
            language === "de" ? "Wird angezeigt von" : "Will be displayed from"
          } ${startDateTime.toLocaleString(
            language === "de" ? "de-DE" : "en-US"
          )} ${language === "de" ? "bis" : "to"} ${endDateTime.toLocaleString(
            language === "de" ? "de-DE" : "en-US"
          )}`,
        });
        setBroadcastData({
          subjectDe: "",
          subjectEn: "",
          messageDe: "",
          messageEn: "",
          type: "info",
          startDate: "",
          startTime: "",
          endDate: "",
          endTime: "",
        });
      } catch (error: any) {
        toast.error(error.response?.data?.message ?? t("common.genericError"));
      }
    };

    void schedule();
  };

  const resetForm = () => {
    setBroadcastData({
      subjectDe: "",
      subjectEn: "",
      messageDe: "",
      messageEn: "",
      type: "info",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
    });
  };

  return (
    <div className="grid gap-6">
      <Card className="stat-card">
        <CardHeader>
          <CardTitle
            className="card-title-text flex items-center gap-2"
            style={primaryTextStyle}
          >
            <MessageSquare className="w-5 h-5" style={iconStyle} />
            {t("admin.broadcastSendToAll")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.broadcastSendDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="broadcast-subject-de" style={primaryTextStyle}>
                {t("admin.subjectRequired")} (DE)
              </Label>
              <Input
                id="broadcast-subject-de"
                placeholder={t("admin.subjectPlaceholder")}
                value={broadcastData.subjectDe}
                onChange={(e) =>
                  setBroadcastData({
                    ...broadcastData,
                    subjectDe: e.target.value,
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="broadcast-subject-en" style={primaryTextStyle}>
                {t("admin.subjectRequired")} (EN)
              </Label>
              <Input
                id="broadcast-subject-en"
                placeholder="e.g. System Maintenance"
                value={broadcastData.subjectEn}
                onChange={(e) =>
                  setBroadcastData({
                    ...broadcastData,
                    subjectEn: e.target.value,
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="broadcast-message-de" style={primaryTextStyle}>
                {t("admin.messageRequired")} (DE)
              </Label>
              <Textarea
                id="broadcast-message-de"
                rows={3}
                placeholder={t("admin.messagePlaceholder")}
                value={broadcastData.messageDe}
                onChange={(e) =>
                  setBroadcastData({
                    ...broadcastData,
                    messageDe: e.target.value,
                  })
                }
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="broadcast-message-en" style={primaryTextStyle}>
                {t("admin.messageRequired")} (EN)
              </Label>
              <Textarea
                id="broadcast-message-en"
                rows={3}
                placeholder="Message text..."
                value={broadcastData.messageEn}
                onChange={(e) =>
                  setBroadcastData({
                    ...broadcastData,
                    messageEn: e.target.value,
                  })
                }
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="broadcast-type" style={primaryTextStyle}>
                {t("admin.bannerTypeRequired")}
              </Label>
              <Select
                value={broadcastData.type}
                onValueChange={(value: any) =>
                  setBroadcastData({
                    ...broadcastData,
                    type: value,
                  })
                }
              >
                <SelectTrigger id="broadcast-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">
                    {t("admin.bannerTypeInfo")}
                  </SelectItem>
                  <SelectItem value="warning">
                    {t("admin.bannerTypeWarning")}
                  </SelectItem>
                  <SelectItem value="alert">
                    {t("admin.bannerTypeAlert")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date" style={primaryTextStyle}>
                  {t("admin.startDateRequired")}
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={broadcastData.startDate}
                  onChange={(e) =>
                    setBroadcastData({
                      ...broadcastData,
                      startDate: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="start-time" style={primaryTextStyle}>
                  {t("admin.startTimeRequired")}
                </Label>
                <Input
                  id="start-time"
                  type="time"
                  value={broadcastData.startTime}
                  onChange={(e) =>
                    setBroadcastData({
                      ...broadcastData,
                      startTime: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="end-date" style={primaryTextStyle}>
                  {t("admin.endDateRequired")}
                </Label>
                <Input
                  id="end-date"
                  type="date"
                  value={broadcastData.endDate}
                  onChange={(e) =>
                    setBroadcastData({
                      ...broadcastData,
                      endDate: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-time" style={primaryTextStyle}>
                  {t("admin.endTimeRequired")}
                </Label>
                <Input
                  id="end-time"
                  type="time"
                  value={broadcastData.endTime}
                  onChange={(e) =>
                    setBroadcastData({
                      ...broadcastData,
                      endTime: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleScheduleBroadcast}
                className="gap-2"
              >
                <Send className="w-4 h-4" />
                {t("admin.scheduleBanner")}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                {t("admin.reset")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="card-title-text" style={primaryTextStyle}>
            {t("admin.scheduledActiveBanners")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.bannersOverview")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm" style={secondaryTextStyle}>
              {t("common.loading")}
            </p>
          ) : broadcasts.length === 0 ? (
            <p className="text-sm" style={secondaryTextStyle}>
              {t("common.noResults")}
            </p>
          ) : (
            <div className="space-y-3">
              {broadcasts.map((broadcast) => {
                const status =
                  new Date(broadcast.endTime) < new Date()
                    ? "completed"
                    : "scheduled";
                const subject = getLocalizedText(
                  broadcast.subjectDe,
                  broadcast.subjectEn
                );
                const message = getLocalizedText(
                  broadcast.messageDe ?? "",
                  broadcast.messageEn
                );
                return (
                  <div key={broadcast.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <h4 style={primaryTextStyle}>{subject}</h4>
                      <Badge
                        variant={status === "scheduled" ? "default" : "secondary"}
                      >
                        {status === "scheduled"
                          ? t("admin.scheduled")
                          : t("admin.completed")}
                      </Badge>
                    </div>
                    <p className="text-sm text-secondary mb-2">{message}</p>
                    <div
                      className="flex items-center gap-4 text-xs"
                      style={secondaryTextStyle}
                    >
                      <span>
                        {t("admin.start")}:{" "}
                        {new Date(broadcast.startTime).toLocaleString(
                          language === "de" ? "de-DE" : "en-US"
                        )}
                      </span>
                      <span>
                        {t("admin.end")}:{" "}
                        {new Date(broadcast.endTime).toLocaleString(
                          language === "de" ? "de-DE" : "en-US"
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
